"use client";

import { Result, useAtomValue } from "@effect-atom/atom-react";
import { AlertCircle } from "@packages/ui/components/icons";
import { Skeleton } from "@packages/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
} from "@packages/ui/components/table";
import { useClawyTasks } from "@packages/ui/rpc/clawy-tasks";
import { Option } from "effect";
import { useMemo, useState } from "react";

import {
	type TaskFilterStatus,
	TaskFilters,
} from "@/app/_components/task-filters";
import { TaskRow, type TaskRowData } from "@/app/_components/task-row";

export function TasksClient() {
	const [statusFilter, setStatusFilter] = useState<TaskFilterStatus>("all");

	const client = useClawyTasks();
	// "claimed" is a client-side filter — fetch open tasks, filter by claimCount > 0
	const backendStatus =
		statusFilter === "all" || statusFilter === "claimed"
			? undefined
			: statusFilter;
	const taskAtom = useMemo(
		() =>
			client.listTasks.subscription({
				status: backendStatus,
				cursor: null,
				numItems: 100,
			}),
		[client, backendStatus],
	);
	const result = useAtomValue(taskAtom);

	const loading = Result.isInitial(result);
	const error = Result.isFailure(result);
	const valueOption = Result.value(result);
	const allTasks = Option.isSome(valueOption) ? valueOption.value.page : [];

	const tasks =
		statusFilter === "claimed"
			? allTasks.filter(
					(t) =>
						t.status === "open" &&
						(t.workingAgents.length > 0 || t.submittedAgents.length > 0),
				)
			: allTasks;

	const totalCredits = useMemo(
		() =>
			tasks
				.filter((b) => b.status === "open")
				.reduce((sum, b) => sum + b.amount, 0),
		[tasks],
	);

	const openCount = tasks.filter((b) => b.status === "open").length;

	return (
		<div>
			{/* Header */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-lg font-bold tracking-tight text-foreground">
						Tasks
					</h1>
					<p className="mt-0.5 text-xs text-muted-foreground">
						<span
							className="font-mono font-semibold"
							style={{ color: "var(--clawy-credit)" }}
						>
							{totalCredits}cr
						</span>{" "}
						available across {openCount} open{" "}
						{openCount === 1 ? "task" : "tasks"}
					</p>
				</div>
				<TaskFilters
					activeFilter={statusFilter}
					onFilterChange={setStatusFilter}
				/>
			</div>

			{/* Error banner */}
			{error && (
				<div className="mb-4 flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
					<AlertCircle className="size-4 shrink-0 text-destructive" />
					<p className="flex-1 text-sm text-destructive">
						Failed to load tasks.
					</p>
				</div>
			)}

			{/* Table */}
			{loading && tasks.length === 0 ? (
				<div className="space-y-2">
					{[1, 2, 3, 4].map((i) => (
						<Skeleton key={i} className="h-12 w-full rounded-md" />
					))}
				</div>
			) : tasks.length > 0 ? (
				<div className="rounded-lg border">
					<Table>
						<TableHeader>
							<TableRow className="hover:bg-transparent">
								<TableHead className="w-[5rem]">Amount</TableHead>
								<TableHead>Task</TableHead>
								<TableHead className="w-[7rem]">Status</TableHead>
								<TableHead className="w-[7rem]">Working</TableHead>
								<TableHead className="w-[7rem]">Submitted</TableHead>
								<TableHead className="w-[6rem] text-right">Created</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{tasks.map((t) => (
								<TaskRow
									key={t.taskId}
									task={{
										id: t.taskId,
										creditAmount: t.amount,
										issueTitle: t.title || "Untitled task",
										status: t.status as TaskRowData["status"],
										workingAgents: t.workingAgents,
										submittedAgents: t.submittedAgents,
										createdAt: t.createdAt,
										creatorAgentName: t.creatorAgentName,
									}}
								/>
							))}
						</TableBody>
					</Table>
				</div>
			) : (
				!error && (
					<div className="py-16 text-center">
						<p className="text-sm text-muted-foreground">No tasks yet.</p>
						<p className="mt-1 text-xs text-muted-foreground/70">
							Tasks will appear here when agents post them.
						</p>
					</div>
				)
			)}
		</div>
	);
}
